import React, { useEffect, useState } from 'react'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import classnames from 'classnames'
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import Panel from '../../../components/Panel'
import Table, { TableColumnConfig } from '../../../components/Table/Table'
import Button from '../../../components/Form/Button'
import Input from '../../../components/Form/Input'
import ComboboxAutocomplete from '../../../components/Form/ComboboxAutocomplete'
import { CustomContentModal } from '../../../components/CustomContentModal'
import VNEMWarning from './VNEMWarning'
import ModifiedDescriptionField from '../programOnboarding/ModifiedDescriptionField'
import { useAppDispatch } from '../../../shared/redux/hooks'
import { pushToast } from '../../../components/Toaster/Toaster.slice'
import { formatDateByText } from '../../tenant/tenant/components/billing/incentiveDisplay'
import {
  formatUTCDate,
  isEndDateOnOrAfterStart,
  NO_PLANNED_END_DATE,
  utcTodayDateOnlyString,
} from '../../../util/string'
import type { GeneratingMeterOption, ProgramGeneratingMeterRow } from '../../../apis/types'
import {
  createProgramGeneratingMeterApi,
  retireProgramGeneratingMeterApi,
  getGeneratingMeterOptionsApi,
  listProgramGeneratingMetersApi,
  updateProgramGeneratingMeterApi,
} from '../../../apis/apis'
import { getApiErrorMessage } from '../../../apis/apiError'

interface ProgramGeneratingMetersPanelProps {
  programId: string
  canModify: boolean
}

type ProgramGeneratingMeterRowWithId = ProgramGeneratingMeterRow & { id: string }

const FALLBACK_ERROR = 'Something went wrong. Please try again.'

// --------------------------------------------------------------------------
// Edit modal — a single existing pairing, dates only (meter is immutable after creation).
// --------------------------------------------------------------------------

interface EditPairingValues {
  startDate: string
  endDate: string
  modifiedDescription: string
}

const requiredMessage = 'Required'

const editValidationSchema = Yup.object({
  startDate: Yup.string().required(requiredMessage),
  endDate: Yup.string().test('end-after-start', 'End date cannot be before start date', function validate(value) {
    if (value == null || value === '') return true
    const { startDate } = this.parent as EditPairingValues
    if (startDate == null || startDate === '') return true
    return isEndDateOnOrAfterStart(value, startDate)
  }),
  modifiedDescription: Yup.string().required(requiredMessage),
})

interface EditPairingModalProps {
  isOpen: boolean
  editingRow: ProgramGeneratingMeterRow
  onClose: () => void
  onSaved: () => void
  programId: string
}

const EditPairingModal: React.FC<EditPairingModalProps> = ({ isOpen, editingRow, onClose, onSaved, programId }) => {
  const dispatch = useAppDispatch()
  const [apiError, setApiError] = useState<string | null>(null)

  const formik = useFormik<EditPairingValues>({
    initialValues: {
      startDate: editingRow.startDate,
      endDate: editingRow.endDate ?? '',
      modifiedDescription: '',
    },
    enableReinitialize: true,
    validationSchema: editValidationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values, { setSubmitting }) => {
      setApiError(null)
      setSubmitting(true)
      try {
        await updateProgramGeneratingMeterApi(programId, editingRow.cuid, {
          startDate: values.startDate,
          endDate: values.endDate === '' ? null : values.endDate,
          modifiedDescription: values.modifiedDescription,
        })
        await dispatch(pushToast({ type: 'success', message: 'Generating meter pairing updated' }))
        onSaved()
        onClose()
      } catch (e: unknown) {
        setApiError(getApiErrorMessage(e, FALLBACK_ERROR))
      } finally {
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    if (isOpen) {
      setApiError(null)
      void formik.resetForm({
        values: {
          startDate: editingRow.startDate,
          endDate: editingRow.endDate ?? '',
          modifiedDescription: '',
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editingRow.cuid])

  return (
    <CustomContentModal isOpen={isOpen} onClose={onClose}>
      <div className="bg-white dark:bg-slate-800 px-5 py-4 w-full max-w-lg mx-auto shadow-xl rounded-2xl flex flex-col">
        <h2 className="mb-4 text-xl font-medium dark:text-white">Edit generating meter pairing</h2>
        <form onSubmit={formik.handleSubmit} className="flex flex-col gap-4">
          <div>
            <div className="font-medium text-slate-700 dark:text-slate-400 mb-1">Generating meter</div>
            <div className="dark:text-white">{editingRow.generatingMeter.meterNumber}</div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Start date"
              type="date"
              required
              value={formik.values.startDate}
              onChange={(e) => formik.setFieldValue('startDate', e.target.value)}
              error={formik.errors.startDate}
            />
            <Input
              label="End date"
              type="date"
              description={`Defaults to ${NO_PLANNED_END_DATE} (no planned end)`}
              value={formik.values.endDate}
              onChange={(e) => formik.setFieldValue('endDate', e.target.value)}
              error={formik.errors.endDate}
            />
          </div>

          <ModifiedDescriptionField
            value={formik.values.modifiedDescription}
            onChange={(value) => formik.setFieldValue('modifiedDescription', value)}
            error={formik.errors.modifiedDescription}
          />

          {apiError != null && <p className="text-sm text-red-600 dark:text-red-500">{apiError}</p>}

          <div className="flex justify-end gap-2 mt-2">
            <Button type="button" color="slate" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" isSaving={formik.isSubmitting}>
              Save changes
            </Button>
          </div>
        </form>
      </div>
    </CustomContentModal>
  )
}

// --------------------------------------------------------------------------
// Retire modal — end-date an in-effect pairing (meter immutable).
// --------------------------------------------------------------------------

interface RetirePairingValues {
  endDate: string
  modifiedDescription: string
}

const retireValidationSchema = Yup.object({
  endDate: Yup.string().required(requiredMessage),
  modifiedDescription: Yup.string().required(requiredMessage),
})

interface RetirePairingModalProps {
  isOpen: boolean
  row: ProgramGeneratingMeterRow
  onClose: () => void
  onRetired: () => void
  programId: string
}

const RetirePairingModal: React.FC<RetirePairingModalProps> = ({ isOpen, row, onClose, onRetired, programId }) => {
  const dispatch = useAppDispatch()
  const [apiError, setApiError] = useState<string | null>(null)
  const defaultEndDate = utcTodayDateOnlyString()

  const formik = useFormik<RetirePairingValues>({
    initialValues: {
      endDate: defaultEndDate,
      modifiedDescription: '',
    },
    enableReinitialize: true,
    validationSchema: retireValidationSchema,
    validate: (values) => {
      const errors: Partial<Record<keyof RetirePairingValues, string>> = {}
      if (!isEndDateOnOrAfterStart(values.endDate, row.startDate)) {
        errors.endDate = 'End date cannot be before start date'
      }
      return errors
    },
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values, { setSubmitting }) => {
      setApiError(null)
      setSubmitting(true)
      try {
        await retireProgramGeneratingMeterApi(programId, row.cuid, {
          endDate: values.endDate,
          modifiedDescription: values.modifiedDescription,
        })
        await dispatch(pushToast({ type: 'success', message: 'Generating meter pairing end-dated' }))
        onRetired()
        onClose()
      } catch (e: unknown) {
        setApiError(getApiErrorMessage(e, FALLBACK_ERROR))
      } finally {
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    if (isOpen) {
      setApiError(null)
      void formik.resetForm({
        values: {
          endDate: utcTodayDateOnlyString(),
          modifiedDescription: '',
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, row.cuid])

  return (
    <CustomContentModal isOpen={isOpen} onClose={onClose}>
      <div className="bg-white dark:bg-slate-800 px-5 py-4 w-full max-w-lg mx-auto shadow-xl rounded-2xl flex flex-col">
        <h2 className="mb-2 text-xl font-medium dark:text-white">End generating meter pairing?</h2>
        <p className="mb-4 text-sm text-slate-600 dark:text-slate-400">
          This end-dates the pairing with {row.generatingMeter.meterNumber}. The row stays in history.
        </p>
        <form onSubmit={formik.handleSubmit} className="flex flex-col gap-4">
          <Input
            label="Final end date"
            type="date"
            required
            min={row.startDate}
            value={formik.values.endDate}
            onChange={(e) => formik.setFieldValue('endDate', e.target.value)}
            error={formik.errors.endDate}
            description="UTC calendar day when this pairing stops"
          />

          <ModifiedDescriptionField
            value={formik.values.modifiedDescription}
            onChange={(value) => formik.setFieldValue('modifiedDescription', value)}
            error={formik.errors.modifiedDescription}
          />

          {apiError != null && <p className="text-sm text-red-600 dark:text-red-500">{apiError}</p>}

          <div className="flex justify-end gap-2 mt-2">
            <Button type="button" color="slate" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" color="red" isSaving={formik.isSubmitting}>
              End pairing
            </Button>
          </div>
        </form>
      </div>
    </CustomContentModal>
  )
}

// --------------------------------------------------------------------------
// Add modal — one or more new pairings in a single submission (coworker feedback: "ability to
// add multiple generating meters at once from the single modal"). Plain component state rather
// than Formik, since rows are added/removed dynamically and each can independently succeed or
// fail against the API.
// --------------------------------------------------------------------------

interface PairingRow {
  /** Stable client-side key for list rendering; unrelated to any server id. */
  key: string
  generatingMeterCuid: string
  startDate: string
  endDate: string
  error?: string
}

let rowKeyCounter = 0
const makeEmptyRow = (): PairingRow => {
  rowKeyCounter += 1
  return { key: `row_${rowKeyCounter}`, generatingMeterCuid: '', startDate: '', endDate: NO_PLANNED_END_DATE }
}

/** Validates every row, attaching a per-row error (including duplicate-meter-across-rows) without calling the API. */
const validateRows = (rows: PairingRow[]): { rows: PairingRow[]; hasErrors: boolean } => {
  const seen = new Set<string>()
  let hasErrors = false
  const validated = rows.map((row) => {
    let error: string | undefined
    if (row.generatingMeterCuid === '') {
      error = requiredMessage
    } else if (seen.has(row.generatingMeterCuid)) {
      error = 'This meter is already selected in another row.'
    } else if (row.startDate === '') {
      error = requiredMessage
    } else if (row.endDate !== '' && !isEndDateOnOrAfterStart(row.endDate, row.startDate)) {
      error = 'End date cannot be before start date'
    }
    if (row.generatingMeterCuid !== '') seen.add(row.generatingMeterCuid)
    if (error != null) hasErrors = true
    return { ...row, error }
  })
  return { rows: validated, hasErrors }
}

interface CreatePairingsModalProps {
  isOpen: boolean
  meterOptions: GeneratingMeterOption[]
  onClose: () => void
  onSaved: () => void
  programId: string
}

const CreatePairingsModal: React.FC<CreatePairingsModalProps> = ({ isOpen, meterOptions, onClose, onSaved, programId }) => {
  const dispatch = useAppDispatch()
  const [rows, setRows] = useState<PairingRow[]>([makeEmptyRow()])
  const [modifiedDescription, setModifiedDescription] = useState('')
  const [formError, setFormError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setRows([makeEmptyRow()])
      setModifiedDescription('')
      setFormError(null)
    }
  }, [isOpen])

  const updateRow = (key: string, patch: Partial<PairingRow>): void => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch, error: undefined } : r)))
  }

  const addRow = (): void => setRows((prev) => [...prev, makeEmptyRow()])

  const removeRow = (key: string): void => setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.key !== key) : prev))

  const optionsForRow = (key: string): GeneratingMeterOption[] => {
    const pickedElsewhere = new Set(rows.filter((r) => r.key !== key && r.generatingMeterCuid !== '').map((r) => r.generatingMeterCuid))
    return meterOptions.filter((m) => !pickedElsewhere.has(m.cuid))
  }

  const handleSubmit = async (): Promise<void> => {
    if (modifiedDescription.trim() === '') {
      setFormError('Change reason is required.')
      return
    }

    const { rows: validated, hasErrors } = validateRows(rows)
    setRows(validated)
    if (hasErrors) {
      return
    }

    setFormError(null)
    setIsSaving(true)
    const remaining: PairingRow[] = []
    let successCount = 0

    for (const row of validated) {
      try {
        await createProgramGeneratingMeterApi(programId, {
          generatingMeterCuid: row.generatingMeterCuid,
          startDate: row.startDate,
          endDate: row.endDate === '' ? null : row.endDate,
          modifiedDescription: modifiedDescription.trim(),
        })
        successCount++
      } catch (e: unknown) {
        remaining.push({ ...row, error: getApiErrorMessage(e, FALLBACK_ERROR) })
      }
    }

    setIsSaving(false)

    if (remaining.length === 0) {
      await dispatch(
        pushToast({ type: 'success', message: successCount === 1 ? 'Generating meter pairing added' : `${successCount} pairings added` })
      )
      onSaved()
      onClose()
      return
    }

    // Successful rows are removed from the form; failed rows stay with their error inline so
    // the user can fix and resubmit just those.
    setRows(remaining)
    if (successCount > 0) {
      await dispatch(
        pushToast({ type: 'success', message: `${successCount} pairing${successCount === 1 ? '' : 's'} added` })
      )
      onSaved()
    }
  }

  return (
    <CustomContentModal isOpen={isOpen} onClose={onClose}>
      <div className="bg-white dark:bg-slate-800 px-5 py-4 w-full max-w-2xl mx-auto shadow-xl rounded-2xl flex flex-col">
        <h2 className="mb-4 text-xl font-medium dark:text-white">Add generating meter pairing</h2>
        <div className="flex flex-col gap-4">
          {rows.map((row, index) => {
            const selectedMeter = meterOptions.find((m) => m.cuid === row.generatingMeterCuid)
            const showGeneratorServiceWarning = selectedMeter != null && !selectedMeter.hasActiveGeneratorMeterService
            return (
              <div key={row.key} className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-500 dark:text-slate-400">Meter {index + 1}</span>
                  {rows.length > 1 && (
                    <button
                      type="button"
                      className="text-xs font-medium text-red-600 dark:text-red-400 hover:underline"
                      onClick={() => removeRow(row.key)}
                    >
                      Remove
                    </button>
                  )}
                </div>

                <ComboboxAutocomplete
                  label="Generating meter"
                  data={optionsForRow(row.key).map((m) => ({
                    id: m.cuid,
                    name: m.meterNumber,
                    description: m.serviceAddress ?? undefined,
                  }))}
                  value={row.generatingMeterCuid || null}
                  handleChange={(value) => updateRow(row.key, { generatingMeterCuid: value ?? '' })}
                  wildCardDisabled
                  isNotFilter
                />

                {showGeneratorServiceWarning && (
                  <VNEMWarning
                    type="warning"
                    text={`${selectedMeter?.meterNumber ?? 'This meter'} has no active GENERATOR meter service yet. The Conductor "ngom_meter_linked" gate will keep failing until that is set up. You can still save this pairing.`}
                  />
                )}

                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Start date"
                    type="date"
                    required
                    value={row.startDate}
                    onChange={(e) => updateRow(row.key, { startDate: e.target.value })}
                  />
                  <Input
                    label="End date"
                    type="date"
                    description={`Defaults to ${NO_PLANNED_END_DATE} (no planned end)`}
                    value={row.endDate}
                    onChange={(e) => updateRow(row.key, { endDate: e.target.value })}
                  />
                </div>

                {row.error != null && <p className="text-xs text-red-600 dark:text-red-500">{row.error}</p>}
              </div>
            )
          })}

          <div>
            <Button type="button" size={1} color="slate" onClick={addRow}>
              Add another meter
            </Button>
          </div>

          <ModifiedDescriptionField value={modifiedDescription} onChange={setModifiedDescription} />

          {formError != null && <p className="text-sm text-red-600 dark:text-red-500">{formError}</p>}

          <div className="flex justify-end gap-2 mt-2">
            <Button type="button" color="slate" onClick={onClose}>
              Cancel
            </Button>
            <Button type="button" isSaving={isSaving} onClick={() => void handleSubmit()}>
              Add pairing{rows.length > 1 ? 's' : ''}
            </Button>
          </div>
        </div>
      </div>
    </CustomContentModal>
  )
}

// --------------------------------------------------------------------------
// Main panel
// --------------------------------------------------------------------------

const ProgramGeneratingMetersPanel: React.FC<ProgramGeneratingMetersPanelProps> = ({ programId, canModify }) => {
  const dispatch = useAppDispatch()
  const [rows, setRows] = useState<ProgramGeneratingMeterRow[] | null>(null)
  const [meterOptions, setMeterOptions] = useState<GeneratingMeterOption[]>([])
  const [formModal, setFormModal] = useState<{ mode: 'create' | 'edit'; row: ProgramGeneratingMeterRow | null } | null>(null)
  const [retireRow, setRetireRow] = useState<ProgramGeneratingMeterRow | null>(null)

  const fetchRows = async (): Promise<void> => {
    try {
      const result = await listProgramGeneratingMetersApi(programId)
      setRows(result)
    } catch (e: unknown) {
      setRows([])
      void dispatch(
        pushToast({ type: 'error', message: getApiErrorMessage(e, 'Could not load generating meter pairings.') })
      )
    }
  }

  useEffect(() => {
    setRows(null)
    void fetchRows()
    if (canModify) {
      void getGeneratingMeterOptionsApi(programId)
        .then(setMeterOptions)
        .catch(() => setMeterOptions([]))
    } else {
      setMeterOptions([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId, canModify])

  const buildColConfig = (): Array<TableColumnConfig<ProgramGeneratingMeterRowWithId>> => {
    const colConfig: Array<TableColumnConfig<ProgramGeneratingMeterRowWithId>> = [
      {
        label: 'Generating Meter',
        render: (row) => (
          <div>
            <div className="font-medium">{row.generatingMeter.meterNumber}</div>
            {row.generatingMeter.serviceAddress != null && (
              <div className="text-xs text-slate-500 dark:text-slate-400">{row.generatingMeter.serviceAddress}</div>
            )}
          </div>
        ),
      },
      {
        label: 'Start Date',
        render: (row) => formatUTCDate(row.startDate),
      },
      {
        label: 'End Date',
        render: (row) => formatUTCDate(row.endDate),
      },
      {
        label: 'Last Modified',
        render: (row) => (
          <div>
            <div>{formatDateByText(row.modifiedAt, row.modifiedBy) ?? row.modifiedBy ?? '—'}</div>
            {row.modifiedDescription != null && (
              <div className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-xs" title={row.modifiedDescription}>
                {row.modifiedDescription}
              </div>
            )}
          </div>
        ),
      },
    ]

    if (canModify) {
      colConfig.push({
        label: 'Actions',
        skipRowLink: true,
        render: (row) => (
          <div className="flex gap-2">
            <Button size={1} color="slate" onClick={() => setFormModal({ mode: 'edit', row })}>
              Edit
            </Button>
            {row.isInEffect && (
              <Button size={1} color="red" onClick={() => setRetireRow(row)}>
                Remove
              </Button>
            )}
          </div>
        ),
      })
    }

    return colConfig
  }

  const colConfig = buildColConfig()
  const allRows = rows ?? []
  const inEffectRows: ProgramGeneratingMeterRowWithId[] = allRows.filter((r) => r.isInEffect).map((r) => ({ ...r, id: r.cuid }))
  const supersededRows: ProgramGeneratingMeterRowWithId[] = allRows.filter((r) => !r.isInEffect).map((r) => ({ ...r, id: r.cuid }))

  return (
    <>
      <Panel
        header={
          <div className="flex items-center justify-between">
            <span>Generating Meters</span>
            {canModify && (
              <Button size={1} onClick={() => setFormModal({ mode: 'create', row: null })}>
                Add pairing
              </Button>
            )}
          </div>
        }
      >
        {rows == null ? (
          <div className="text-sm text-slate-500 dark:text-slate-400">Loading generating meters...</div>
        ) : allRows.length === 0 ? (
          <div className="flex flex-col items-start gap-2 py-4">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              No generating meters are paired to this program yet.{canModify ? ' Add one to get started.' : ''}
            </p>
            {canModify && (
              <Button size={1} onClick={() => setFormModal({ mode: 'create', row: null })}>
                Add pairing
              </Button>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              The legacy <code>generating_meter_cuid</code> on Program Meters can only approximate a multi-generator program.
              This panel is the source of truth for generating meter pairings going forward.
            </p>

            {inEffectRows.length > 0 && (
              <div>
                {inEffectRows.length > 1 && (
                  <p className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">
                    {inEffectRows.length} generators currently in effect together — a pooled / combined-POI arrangement, not
                    duplicate rows.
                  </p>
                )}
                <Table
                  colConfig={colConfig}
                  data={{ results: inEffectRows, total: inEffectRows.length, page: 0, resultsPerPage: inEffectRows.length }}
                  hidePaginator
                />
              </div>
            )}

            {supersededRows.length > 0 && (
              <Disclosure defaultOpen={false}>
                {({ open }) => (
                  <div>
                    <DisclosureButton className="flex w-full items-center justify-between gap-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-4 py-2 text-left text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700">
                      <span>Superseded pairings ({supersededRows.length})</span>
                      <ChevronDownIcon className={classnames('w-4 h-4 text-slate-400 transition-transform', open && 'rotate-180')} />
                    </DisclosureButton>
                    <DisclosurePanel className="mt-2">
                      <Table
                        colConfig={colConfig}
                        data={{
                          results: supersededRows,
                          total: supersededRows.length,
                          page: 0,
                          resultsPerPage: supersededRows.length,
                        }}
                        hidePaginator
                        rowClassName={() => 'opacity-60'}
                      />
                    </DisclosurePanel>
                  </div>
                )}
              </Disclosure>
            )}
          </div>
        )}
      </Panel>

      {formModal?.mode === 'create' && (
        <CreatePairingsModal
          isOpen={formModal != null}
          meterOptions={meterOptions}
          programId={programId}
          onClose={() => setFormModal(null)}
          onSaved={() => void fetchRows()}
        />
      )}

      {formModal?.mode === 'edit' && formModal.row != null && (
        <EditPairingModal
          isOpen={formModal != null}
          editingRow={formModal.row}
          programId={programId}
          onClose={() => setFormModal(null)}
          onSaved={() => void fetchRows()}
        />
      )}

      {retireRow != null && (
        <RetirePairingModal
          isOpen={retireRow != null}
          row={retireRow}
          programId={programId}
          onClose={() => setRetireRow(null)}
          onRetired={() => void fetchRows()}
        />
      )}
    </>
  )
}

export default ProgramGeneratingMetersPanel
